import { useMemo } from "react";

import {
  PortfolioAdditionalStatTile,
  PortfolioCardShell,
  PortfolioCardsList,
  PortfolioMetricCard,
  PortfolioTrialHeader,
} from "@/views/Portfolio/components";
import type {
  PortfolioAdditionalStat,
  PortfolioMetricView,
  PortfolioTrialHeaderInfo,
  TrialInfo,
} from "@/views/Portfolio/types";
import {
  buildPerSiteAdditionalStat,
  derivePortfolioMetricView,
  indexTrialsById,
  resolvePortfolioTrialHeader,
} from "@/views/Portfolio/utils";
import { formatDecimal } from "@/utils/numberFormatting";

type SafetyRow = {
  trial_id: string;
  subjects_with_aes: number;
  subjects_with_aes_delta_30d?: number | null;
  subjects_with_saes: number;
  subjects_with_saes_delta_30d?: number | null;
  et_due_to_ae: number;
  et_due_to_ae_delta_30d?: number | null;
  deaths: number;
  deaths_delta_30d?: number | null;
  aes_per_patient_year: number;
  aes_per_patient_year_delta_30d?: number | null;
  per_site_subjects_with_aes_avg?: number | null;
  per_site_subjects_with_aes_sd?: number | null;
  per_site_subjects_with_aes_range_low?: number | null;
  per_site_subjects_with_aes_range_high?: number | null;
  per_site_subjects_with_saes_avg?: number | null;
  per_site_subjects_with_saes_sd?: number | null;
  per_site_subjects_with_saes_range_low?: number | null;
  per_site_subjects_with_saes_range_high?: number | null;
  per_site_et_due_to_ae_avg?: number | null;
  per_site_et_due_to_ae_sd?: number | null;
  per_site_et_due_to_ae_range_low?: number | null;
  per_site_et_due_to_ae_range_high?: number | null;
  per_site_deaths_avg?: number | null;
  per_site_deaths_sd?: number | null;
  per_site_deaths_range_low?: number | null;
  per_site_deaths_range_high?: number | null;
  per_site_aes_per_patient_year_avg?: number | null;
  per_site_aes_per_patient_year_sd?: number | null;
  per_site_aes_per_patient_year_range_low?: number | null;
  per_site_aes_per_patient_year_range_high?: number | null;
};

const SAFETY_ADDITIONAL_STATS = [
  { key: "subjects-with-aes", unit: "%" },
  { key: "subjects-with-saes", unit: "%" },
  { key: "et-due-to-ae", unit: "%" },
  { key: "deaths", unit: undefined },
  { key: "aes-per-patient-year", unit: "AEs/patient-year" },
] as const;

type SafetyCard = PortfolioTrialHeaderInfo & {
  subjectsWithAesView: PortfolioMetricView;
  subjectsWithSaesView: PortfolioMetricView;
  earlyTerminationDueToAeView: PortfolioMetricView;
  deathsView: PortfolioMetricView;
  aesPerPatientYearView: PortfolioMetricView;
  additionalStats: PortfolioAdditionalStat[];
  initiallyExpanded: boolean;
};

function buildSafetyMetricView(
  actual: number,
  delta30d: number | null | undefined,
  isComplete: boolean,
  round: boolean,
): PortfolioMetricView {
  const rawDelta = isComplete ? null : (delta30d ?? null);
  return derivePortfolioMetricView({
    actual,
    planned: { value: null, date: null, frequency: null },
    total: null,
    delta30d:
      rawDelta === null ? null : round ? Math.round(rawDelta) : rawDelta,
    lifecycle: isComplete ? "final" : "active",
    planTracked: false,
  });
}

function SafetyCardView({ card }: { card: SafetyCard }) {
  return (
    <PortfolioCardShell
      header={
        <PortfolioTrialHeader
          trial={card}
          titleClassName="!font-bold"
          linkToTrial
        />
      }
      metricColumns={5}
      initiallyExpanded={card.initiallyExpanded}
      additionalContent={card.additionalStats.map((stat, index) => (
        <PortfolioAdditionalStatTile
          key={`${card.trial_id}-${SAFETY_ADDITIONAL_STATS[index].key}`}
          stat={stat}
          unit={SAFETY_ADDITIONAL_STATS[index].unit}
        />
      ))}
    >
      <PortfolioMetricCard
        label="SUBJECTS WITH AEs"
        view={card.subjectsWithAesView}
        suffix="%"
        formatValue={(value) => formatDecimal(value, 0)}
        progressTone="magenta"
        invertDelta
      />
      <PortfolioMetricCard
        label="SUBJECTS WITH SAEs"
        view={card.subjectsWithSaesView}
        suffix="%"
        formatValue={(value) => formatDecimal(value, 0)}
        progressTone="magenta"
        invertDelta
      />
      <PortfolioMetricCard
        label="ET DUE TO AE"
        view={card.earlyTerminationDueToAeView}
        suffix="%"
        formatValue={(value) => formatDecimal(value, 0)}
        progressTone="gold"
        invertDelta
      />
      <PortfolioMetricCard
        label="DEATHS"
        view={card.deathsView}
        formatValue={(value) => formatDecimal(value, 0)}
        invertDelta
      />
      <PortfolioMetricCard
        label="AEs PER PATIENT-YEAR"
        view={card.aesPerPatientYearView}
        formatValue={(value) => formatDecimal(value, 2)}
        invertDelta
      />
    </PortfolioCardShell>
  );
}

function buildSafetyCard(
  row: SafetyRow,
  trialsById: Map<string, TrialInfo>,
): SafetyCard {
  const trial = trialsById.get(row.trial_id);
  const header = resolvePortfolioTrialHeader(row.trial_id, trial);

  return {
    ...header,
    subjectsWithAesView: buildSafetyMetricView(
      row.subjects_with_aes,
      row.subjects_with_aes_delta_30d,
      header.isComplete,
      true,
    ),
    subjectsWithSaesView: buildSafetyMetricView(
      row.subjects_with_saes,
      row.subjects_with_saes_delta_30d,
      header.isComplete,
      true,
    ),
    earlyTerminationDueToAeView: buildSafetyMetricView(
      row.et_due_to_ae,
      row.et_due_to_ae_delta_30d,
      header.isComplete,
      true,
    ),
    deathsView: buildSafetyMetricView(
      row.deaths,
      row.deaths_delta_30d,
      header.isComplete,
      true,
    ),
    aesPerPatientYearView: buildSafetyMetricView(
      row.aes_per_patient_year,
      row.aes_per_patient_year_delta_30d,
      header.isComplete,
      false,
    ),
    additionalStats: [
      buildPerSiteAdditionalStat(
        row.per_site_subjects_with_aes_avg,
        row.per_site_subjects_with_aes_sd,
        row.per_site_subjects_with_aes_range_low,
        row.per_site_subjects_with_aes_range_high,
        1,
      ),
      buildPerSiteAdditionalStat(
        row.per_site_subjects_with_saes_avg,
        row.per_site_subjects_with_saes_sd,
        row.per_site_subjects_with_saes_range_low,
        row.per_site_subjects_with_saes_range_high,
        1,
      ),
      buildPerSiteAdditionalStat(
        row.per_site_et_due_to_ae_avg,
        row.per_site_et_due_to_ae_sd,
        row.per_site_et_due_to_ae_range_low,
        row.per_site_et_due_to_ae_range_high,
        1,
      ),
      buildPerSiteAdditionalStat(
        row.per_site_deaths_avg,
        row.per_site_deaths_sd,
        row.per_site_deaths_range_low,
        row.per_site_deaths_range_high,
        1,
      ),
      buildPerSiteAdditionalStat(
        row.per_site_aes_per_patient_year_avg,
        row.per_site_aes_per_patient_year_sd,
        row.per_site_aes_per_patient_year_range_low,
        row.per_site_aes_per_patient_year_range_high,
        2,
      ),
    ],
    initiallyExpanded: false,
  };
}

const buildCards = (rows: SafetyRow[], trialsById: Map<string, TrialInfo>) =>
  rows.map((row) => buildSafetyCard(row, trialsById));

export function PortfolioSafetyTrendsTab({
  data,
  trials,
}: {
  data?: SafetyRow[];
  trials?: TrialInfo[];
}) {
  const cards = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const trialSource = trials ?? [];
    const trialsById = indexTrialsById(trialSource);
    return buildCards(rows, trialsById);
  }, [data, trials]);

  return (
    <PortfolioCardsList>
      {cards.map((card) => (
        <SafetyCardView key={card.trial_id} card={card} />
      ))}
    </PortfolioCardsList>
  );
}
